import type { ScheduledDetailedCopy } from './scheduled-page-copy-translations';

export const scheduledDetailedCopyJapaneseKorean = {
  ja: {
    status: {
      upcoming: '予定あり',
      running: '実行中',
      waitingBackground: 'バックグラウンド処理を待機中',
      succeeded: '成功',
      failed: '失敗',
      skipped: 'スキップ済み',
      needsAttention: '確認が必要',
      unknownOutcome: '結果不明',
      canceled: 'キャンセル済み',
      malformed: '利用不可',
      paused: '一時停止中',
    },
    editor: {
      createTitle: '新しいスケジュール',
      editTitle: 'スケジュールを編集',
      description: 'タスク、時刻、コンテキスト、無人実行の権限を設定します。',
      stepOf: '4 ステップ中 {step}',
      task: 'タスク',
      timing: '実行時刻',
      execution: '実行方法',
      review: '確認',
      name: 'スケジュール名',
      namePlaceholder: '例：月曜日の業績概要',
      instructions: 'エージェントへの指示',
      instructionsPlaceholder:
        'エージェントが作成する結果と、操作を許可する範囲を記述してください。',
      sourceChat: '元のチャット',
      scheduleThisChat: 'このチャットをスケジュール',
      sourceSnapshot:
        '保存時に非公開のスナップショットを取得します。その後のチャット変更はこのスケジュールに反映されません。',
      sourceUnavailable:
        '元のチャットが利用できないか、アクセス権がありません。',
      sourceChatLockedNote:
        '元のチャットを再利用するスケジュールでは、コンテキストを継続し、公開範囲を非公開にする必要があります。',
      recurrence: '繰り返し',
      once: '1 回のみ',
      hourly: '毎時',
      daily: '毎日',
      weekdays: '平日',
      selectedWeekdays: '選択した曜日',
      weekly: '毎週',
      monthly: '毎月',
      date: '日付',
      time: '現地時刻',
      dayOfMonth: '毎月の日付',
      whenPlaceholder: '明日',
      whenInvalid: '「明日」のように日付を入力してください。',
      end: '終了条件',
      never: '終了しない',
      onDate: '指定日に終了',
      afterOccurrences: '指定回数の実行後',
      occurrences: '実行回数',
      nextRuns: '次の実行',
      schedulePreview: 'スケジュールのプレビュー',
      noEndDate: '終了日はありません。',
      runsUntil: '{date}まで実行します。',
      totalRuns: '合計{count}回実行します。',
      resolvingTime: 'サーバーで現地時刻を確認しています…',
      serverTimeRequired:
        '保存するには、サーバーによる現地時刻の確認が必要です。',
      contextMode: 'チャットのコンテキスト',
      persistent: '継続コンテキスト',
      persistentDescription:
        'スケジュール専用の 1 つのチャットをすべての実行で再利用します。',
      isolated: '実行ごとに新しいチャット',
      isolatedDescription: '実行ごとに独立したコンテキストで開始します。',
      visibility: '公開範囲',
      private: '非公開',
      privateDescription:
        '指示全文、会話、ツール操作、結果を閲覧できるのはあなただけです。',
      workspace: 'ワークスペース',
      workspaceWarning:
        'ワークスペースに共有すると、指示全文、会話、ツール操作、結果がすべて公開されます。',
      creditPolicy: 'ワークスペースのクレジット方針',
      creditsAvailable:
        '実行にはワークスペースの現在のクレジット残高と上限が適用されます。',
      creditsUnavailable:
        'このワークスペースには現在利用可能なクレジットがありません。実行時にスケジュールが一時停止される場合があります。',
      fullAuthorityTitle: 'エージェントの操作',
      fullAuthorityDescription:
        '実行のたびに現在のアクセス権を再確認し、確認なしで破壊的な操作を行う場合があります。',
      authorityAcknowledgment: '事前確認なしですべての操作を許可します。',
      authorityRequired:
        'エージェントの操作を許可するとこのスケジュールを作成できます。実行は無人で行われるため、後から承認できる人はいません。',
      recurrenceUnresolved:
        'この実行時刻を確認できませんでした。日付と時刻を選び直してください。',
      notifications: 'アプリ内通知',
      notificationsDescription: '確認が必要な最終結果ごとに 1 回通知します。',
      reviewSummary:
        '作成する前に、タスクの内容と実行頻度を正確に確認してください。',
      required: 'この項目は必須です。',
      invalid: '有効な値を入力してください。',
      past: '未来の時刻を選択してください。',
      endBeforeStart: '最初の実行より後に終了するよう設定してください。',
      draftRestored: '保存されていない下書きを復元しました。',
      tooLong: '入力内容が長すぎます。',
      createDisabled:
        'このワークスペースでスケジュールを作成する権限がありません。',
      saved: 'スケジュールを保存しました',
      unsavedTitle: '未保存の変更を破棄しますか？',
      unsavedDescription:
        'このまま閉じると、スケジュールへの変更は失われます。',
      discardChanges: '変更を破棄',
    },
    detail: {
      title: 'スケジュールの詳細',
      cadence: '実行頻度',
      owner: '所有者',
      timezone: 'タイムゾーン',
      nextRun: '次の実行',
      lastRun: '前回の実行',
      prompt: '指示全文',
      sourceContext: '保存済みの元コンテキスト',
      executionMode: '実行モード',
      recentUsage: '最近の使用量',
      credits: 'クレジット',
      tools: 'アクション',
      history: '実行履歴',
      transcript: '会話',
      resultFiles: '結果ファイル',
      noResultFiles: 'この実行では結果ファイルが記録されていません。',
      noRuns: '実行履歴はまだありません。',
      noTranscript: 'この実行の会話はまだありません。',
      toolActivity: 'アクティビティ',
      toolInput: 'タスクの詳細',
      toolResult: '結果',
      noToolActivity: 'この実行のアクティビティはありません。',
      loadMore: 'さらに読み込む',
      preview: 'プレビュー',
      sourceScheduled: '予定実行',
      sourceManual: '手動実行',
      sourceWatchdog: '復旧実行',
      roleUser: 'ユーザー',
      roleAssistant: 'アシスタント',
      roleTool: 'ツール',
      roleSystem: 'システム',
      runNow: '今すぐ実行',
      pause: '一時停止',
      resume: '再開',
      share: 'ワークスペースに共有',
      unshare: '非公開にする',
      cancelRun: '実行中の処理をキャンセル',
      archive: 'アーカイブ',
      deleteSchedule: 'スケジュールを削除',
      openThread: 'スレッドを表示',
      confirmArchive: 'このスケジュールをアーカイブしますか？',
      confirmArchiveDescription:
        '今後の処理はキャンセルされます。履歴と監査記録は保持されます。',
      confirmDelete: 'このスケジュールを削除しますか？',
      confirmDeleteDescription:
        'このスケジュールは完全に削除されます。過去の会話と生成した成果物はワークスペースに残ります。',
      confirmUnshareDescription:
        'ワークスペースのメンバーは、プロンプト、トランスクリプト、ツールのアクティビティ、結果にすぐアクセスできなくなります。',
      confirmCancel: '実行中の処理をキャンセルしますか？',
      confirmCancelDescription: '次の安全なキャンセル地点で実行を停止します。',
      actionCompleted: 'スケジュールを更新しました',
      permissionDenied:
        'このスケジュールは表示できますが、変更できるのは所有者またはスケジュール管理者だけです。',
      needsAttention:
        '無人実行を続ける前に、この実行を確認する必要があります。',
      runDetails: '実行の詳細',
    },
    notifications: {
      completed: 'スケジュール実行が完了しました',
      failed: 'スケジュール実行に失敗しました',
      attention: 'スケジュール実行の確認が必要です',
      missed: '1 回のみのスケジュール実行を逃しました',
      viewRun: '実行を表示',
      empty: '実行通知はまだありません。',
      unread: '未読',
      read: '既読',
      caughtUp: 'すべての通知を確認しました。',
    },
    errors: {
      generic: '問題が発生しました。もう一度お試しください。',
      permission: 'この操作を行う権限がありません。',
      disabled:
        'このワークスペースではスケジュールエージェントがまだ有効になっていません。',
      credits: 'ワークスペースのクレジットが不足しています。',
      limit: 'ワークスペースの有効なスケジュール数が上限に達しました。',
      runPending:
        'このスケジュールには、待機中または実行中の処理がすでにあります。',
      invalidTime: '将来の有効な時刻を選択してください。',
      notFound: 'このスケジュールまたは実行は利用できなくなりました。',
    },
  },
  ko: {
    status: {
      upcoming: '예정됨',
      running: '실행 중',
      waitingBackground: '백그라운드 작업 대기 중',
      succeeded: '성공',
      failed: '실패',
      skipped: '건너뜀',
      needsAttention: '확인 필요',
      unknownOutcome: '결과 알 수 없음',
      canceled: '취소됨',
      malformed: '사용할 수 없음',
      paused: '일시 중지됨',
    },
    editor: {
      createTitle: '새 일정',
      editTitle: '일정 편집',
      description: '작업, 시간, 문맥, 무인 실행 권한을 설정합니다.',
      stepOf: '4단계 중 {step}단계',
      task: '작업',
      timing: '시간',
      execution: '실행',
      review: '검토',
      name: '일정 이름',
      namePlaceholder: '예: 월요일 성과 요약',
      instructions: '에이전트 지침',
      instructionsPlaceholder:
        '에이전트가 만들어야 할 결과와 작업을 허용할 범위를 설명하세요.',
      sourceChat: '원본 채팅',
      scheduleThisChat: '이 채팅 예약',
      sourceSnapshot:
        '저장할 때 비공개 스냅샷을 만듭니다. 이후 채팅 변경 사항은 이 일정에 반영되지 않습니다.',
      sourceUnavailable:
        '원본 채팅을 사용할 수 없거나 더 이상 접근 권한이 없습니다.',
      sourceChatLockedNote:
        '원본 채팅을 재사용하는 일정은 문맥을 계속 유지하고 공개 범위를 비공개로 설정해야 합니다.',
      recurrence: '반복',
      once: '한 번',
      hourly: '매시간',
      daily: '매일',
      weekdays: '평일',
      selectedWeekdays: '선택한 요일',
      weekly: '매주',
      monthly: '매월',
      date: '날짜',
      time: '현지 시간',
      dayOfMonth: '매월 날짜',
      whenPlaceholder: 'YYYY-MM-DD',
      whenInvalid: '날짜를 YYYY-MM-DD 형식으로 입력하세요.',
      end: '종료',
      never: '종료 안 함',
      onDate: '지정한 날짜',
      afterOccurrences: '지정한 실행 횟수 후',
      occurrences: '실행 횟수',
      nextRuns: '다음 실행',
      schedulePreview: '일정 미리보기',
      noEndDate: '종료 날짜가 없습니다.',
      runsUntil: '{date}까지 실행합니다.',
      totalRuns: '총 {count}회 실행합니다.',
      resolvingTime: '서버에서 이 현지 시간을 확인하고 있습니다…',
      serverTimeRequired: '저장하려면 서버가 이 현지 시간을 확인해야 합니다.',
      contextMode: '채팅 문맥',
      persistent: '지속 문맥',
      persistentDescription:
        '일정 전용 채팅 하나를 모든 실행에서 재사용합니다.',
      isolated: '실행마다 새 채팅',
      isolatedDescription: '각 실행을 분리된 문맥으로 시작합니다.',
      visibility: '공개 범위',
      private: '비공개',
      privateDescription:
        '전체 요청, 대화, 도구 활동 및 결과는 본인만 볼 수 있습니다.',
      workspace: '작업 공간',
      workspaceWarning:
        '작업 공간에 공유하면 전체 요청, 대화, 도구 활동 및 결과가 공개됩니다.',
      creditPolicy: '작업 공간 크레딧 정책',
      creditsAvailable:
        '실행에는 작업 공간의 현재 크레딧 잔액과 한도가 적용됩니다.',
      creditsUnavailable:
        '현재 이 작업 공간에는 사용 가능한 크레딧이 없습니다. 실행 시 일정이 일시 중지될 수 있습니다.',
      fullAuthorityTitle: '에이전트 작업',
      fullAuthorityDescription:
        '실행할 때마다 현재 접근 권한을 다시 확인하며, 묻지 않고 파괴적인 작업을 수행할 수 있습니다.',
      authorityAcknowledgment: '먼저 묻지 않고 모든 작업을 허용합니다.',
      authorityRequired:
        '이 일정을 만들려면 에이전트 작업을 허용하세요. 실행은 무인으로 진행되므로 나중에 누구도 승인할 수 없습니다.',
      recurrenceUnresolved:
        '이 실행 시각을 확인할 수 없습니다. 날짜와 시간을 다시 선택하세요.',
      notifications: '앱 내 알림',
      notificationsDescription:
        '확인이 필요한 최종 결과마다 한 번만 알림을 보냅니다.',
      reviewSummary: '일정을 만들기 전에 정확한 작업과 주기를 검토하세요.',
      required: '필수 항목입니다.',
      invalid: '올바른 값을 입력하세요.',
      past: '미래 시각을 선택하세요.',
      endBeforeStart: '첫 실행 이후에 종료되도록 설정하세요.',
      draftRestored: '저장하지 않은 초안을 복원했습니다.',
      tooLong: '입력값이 너무 깁니다.',
      createDisabled: '이 작업 공간에서 일정을 만들 권한이 없습니다.',
      saved: '일정이 저장되었습니다',
      unsavedTitle: '저장하지 않은 변경 사항을 버릴까요?',
      unsavedDescription: '지금 닫으면 이 일정의 변경 사항이 사라집니다.',
      discardChanges: '변경 사항 버리기',
    },
    detail: {
      title: '일정 세부 정보',
      cadence: '실행 주기',
      owner: '소유자',
      timezone: '시간대',
      nextRun: '다음 실행',
      lastRun: '최근 실행',
      prompt: '전체 요청',
      sourceContext: '저장된 원본 문맥',
      executionMode: '실행 모드',
      recentUsage: '최근 사용량',
      credits: '크레딧',
      tools: '작업',
      history: '실행 기록',
      transcript: '대화',
      resultFiles: '결과 파일',
      noResultFiles: '이 실행에 기록된 결과 파일이 없습니다.',
      noRuns: '아직 실행 기록이 없습니다.',
      noTranscript: '이 실행의 대화가 아직 없습니다.',
      toolActivity: '활동',
      toolInput: '작업 세부 정보',
      toolResult: '결과',
      noToolActivity: '이 실행에는 활동이 없습니다.',
      loadMore: '더 불러오기',
      preview: '미리보기',
      sourceScheduled: '예약 실행',
      sourceManual: '직접 실행',
      sourceWatchdog: '복구 실행',
      roleUser: '사용자',
      roleAssistant: '어시스턴트',
      roleTool: '도구',
      roleSystem: '시스템',
      runNow: '지금 실행',
      pause: '일시 중지',
      resume: '다시 시작',
      share: '작업 공간에 공유',
      unshare: '비공개로 전환',
      cancelRun: '활성 실행 취소',
      archive: '보관',
      deleteSchedule: '일정 삭제',
      openThread: '스레드 보기',
      confirmArchive: '이 일정을 보관할까요?',
      confirmArchiveDescription:
        '향후 작업은 취소되며 기록과 감사 자료는 보존됩니다.',
      confirmDelete: '이 일정을 삭제할까요?',
      confirmDeleteDescription:
        '이 일정은 영구적으로 삭제됩니다. 이전 대화와 생성된 작업은 워크스페이스에 남습니다.',
      confirmUnshareDescription:
        '작업 공간 구성원은 프롬프트, 대화 기록, 도구 활동 및 결과에 즉시 액세스할 수 없게 됩니다.',
      confirmCancel: '활성 실행을 취소할까요?',
      confirmCancelDescription: '다음 안전한 취소 지점에서 실행이 중지됩니다.',
      actionCompleted: '일정이 업데이트되었습니다',
      permissionDenied:
        '이 일정은 볼 수 있지만 소유자 또는 일정 관리자만 변경할 수 있습니다.',
      needsAttention: '무인 작업을 계속하기 전에 이 실행을 검토해야 합니다.',
      runDetails: '실행 세부 정보',
    },
    notifications: {
      completed: '예약 실행이 완료되었습니다',
      failed: '예약 실행에 실패했습니다',
      attention: '예약 실행을 확인해야 합니다',
      missed: '한 번만 실행되는 예약을 놓쳤습니다',
      viewRun: '실행 보기',
      empty: '실행 알림이 아직 없습니다.',
      unread: '읽지 않음',
      read: '읽음',
      caughtUp: '모든 알림을 확인했습니다.',
    },
    errors: {
      generic: '문제가 발생했습니다. 다시 시도하세요.',
      permission: '이 작업을 수행할 권한이 없습니다.',
      disabled: '이 작업 공간에는 예약 에이전트가 아직 활성화되지 않았습니다.',
      credits: '작업 공간에 크레딧이 부족합니다.',
      limit: '작업 공간의 활성 일정 수가 한도에 도달했습니다.',
      runPending: '이 일정에는 이미 대기 중이거나 실행 중인 작업이 있습니다.',
      invalidTime: '올바른 미래 시간을 선택하세요.',
      notFound: '이 일정 또는 실행을 더 이상 사용할 수 없습니다.',
    },
  },
} satisfies Record<'ja' | 'ko', ScheduledDetailedCopy>;
